import { css } from "@emotion/css";
import { ChannelIcon, ParentEnvironmentIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontSize, fontWeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ChannelResource, ParentEnvironmentResource } from "@octopusdeploy/octopus-server-client";
import classNames from "classnames";
import * as React from "react";
import { getAutomaticDeprovisioningRuleDescription } from "~/areas/infrastructure/components/ParentEnvironmentLayout/getAutomaticDeprovisioningRuleDescription";
interface ParentEnvironmentConfigurationCardProps {
    parentEnvironment: ParentEnvironmentResource;
    onClick?: () => void;
}
export function ParentEnvironmentConfigurationCard({ parentEnvironment, onClick }: ParentEnvironmentConfigurationCardProps) {
    return (<ConfigurationCard icon={<ParentEnvironmentIcon size={20}/>} title={parentEnvironment.Name} onClick={onClick} details={[{ title: "Automatic deprovisioning", value: getAutomaticDeprovisioningRuleDescription(parentEnvironment.AutomaticDeprovisioningRule) }]}/>);
}
interface ChannelConfigurationCardProps {
    channel: ChannelResource;
    parentEnvironment: ParentEnvironmentResource;
    onClick?: () => void;
}
export function ChannelConfigurationCard({ channel, parentEnvironment, onClick }: ChannelConfigurationCardProps) {
    const details: Array<{
        title: string;
        value: string;
    }> = [{ title: "Parent environment", value: parentEnvironment.Name }];
    if (channel.AutomaticEphemeralEnvironmentDeployments) {
        details.push({ title: "Auto Deployment", value: "Automatically deploy to ephemeral environments" }, { title: "Environment Name Template", value: channel.EphemeralEnvironmentNameTemplate || "" });
    }
    else {
        details.push({ title: "Auto Deployment", value: "Don't automatically deploy to ephemeral environments" });
    }
    return <ConfigurationCard icon={<ChannelIcon size={20}/>} title={channel.Name} details={details} onClick={onClick}/>;
}
interface ChannelConfigurationCardWithRawInputsProps {
    channelName: string;
    parentEnvironmentName: string;
    ephemeralEnvironmentNameTemplate: string;
    automaticEphemeralEnvironmentDeployments?: boolean;
}
export function ChannelConfigurationCardWithRawInputs({ channelName, parentEnvironmentName, automaticEphemeralEnvironmentDeployments, ephemeralEnvironmentNameTemplate }: ChannelConfigurationCardWithRawInputsProps) {
    const details: Array<{
        title: string;
        value: string;
    }> = [{ title: "Parent environment", value: parentEnvironmentName }];
    details.push({ title: "Auto deployments", value: automaticEphemeralEnvironmentDeployments ? "Automatically deploy to ephemeral environments" : "Don't automatically deploy to ephemeral environments" });
    if (automaticEphemeralEnvironmentDeployments) {
        details.push({ title: "Name template", value: ephemeralEnvironmentNameTemplate });
    }
    return <ConfigurationCard icon={<ChannelIcon size={20}/>} title={channelName} details={details}/>;
}
interface ConfigurationCardProps {
    icon: React.ReactNode;
    title: string;
    details?: Array<{
        title: string;
        value: string;
    }>;
    onClick?: () => void;
}
function ConfigurationCard({ icon, title, details, onClick }: ConfigurationCardProps) {
    details = details || [];
    const styles = {
        container: css({
            border: "1px solid",
            borderColor: themeTokens.color.border.primary,
            color: themeTokens.color.text.primary,
            borderRadius: borderRadius.small,
            padding: space[16],
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gridTemplateRows: "auto auto",
            gap: space[8],
        }),
        clickableContainer: css({
            backgroundColor: themeTokens.color.background.primary.default,
            textAlign: "left",
            width: "100%",
            "&:hover": { backgroundColor: themeTokens.color.background.primary.hover },
        }),
        icon: css({
            gridRow: 1,
            gridColumn: 1,
            color: themeTokens.color.text.link.default,
        }),
        title: css({
            gridRow: 1,
            gridColumn: 2,
            alignSelf: "center",
            fontSize: fontSize.base,
            color: themeTokens.color.text.link.default,
        }),
        property: css({
            gridRow: 2,
            gridColumn: 2,
            fontSize: fontSize.medium,
        }),
        propertyValue: css({
            fontWeight: fontWeight[700],
        }),
    };
    const innerContent = (<>
            <div className={styles.icon}>{icon}</div>
            <div className={styles.title}>{title}</div>
            <div className={styles.property}>
                {details.map((detail, index) => (<div key={index}>
                        {detail.title}: <span className={styles.propertyValue}>{detail.value}</span>
                    </div>))}
            </div>
        </>);
    if (onClick) {
        return (<button className={classNames(styles.container, styles.clickableContainer)} onClick={onClick}>
                {innerContent}
            </button>);
    }
    return <div className={styles.container}>{innerContent}</div>;
}
